import type { Plugin } from "../../../core/plugin";
import { PluginRegistry } from "../../../core/pluginRegistry";

/**
 * ltnNav — navegación de cabecera para la web corporativa de LTN (LTNW-1).
 *
 * Sustituye los handlers `toggleMenu` / `closeMenu` de React del prototipo y
 * el condicional `<sc-if value="{{ menuOpen }}">`. JS vanilla sin handlers
 * inline, para que la página se pueda publicar bajo la CSP del dominio WMP.
 *
 * Contrato de marcado:
 *   <header data-plugin="ltnNav">
 *     <button data-ltn-nav-toggle aria-expanded="false" aria-controls="…">
 *     <div data-ltn-nav-drawer id="…">   ← los enlaces cierran el drawer al hacer clic
 */
export class LtnNav implements Plugin {
  private toggle: HTMLButtonElement | null = null;
  private drawer: HTMLElement | null = null;
  private isOpen = false;

  private readonly onToggleClick = () => this.setOpen(!this.isOpen);

  private readonly onDrawerClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("a")) this.setOpen(false);
  };

  private readonly onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.isOpen) {
      this.setOpen(false);
      this.toggle?.focus();
    }
  };

  private readonly onDocumentClick = (event: MouseEvent) => {
    if (this.isOpen && !this.element.contains(event.target as Node)) {
      this.setOpen(false);
    }
  };

  /** Por encima del breakpoint del burger, el drawer nunca debe quedarse abierto. */
  private readonly onResize = () => {
    if (this.isOpen && window.innerWidth > 1120) this.setOpen(false);
  };

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    this.toggle = this.element.querySelector("[data-ltn-nav-toggle]");
    this.drawer = this.element.querySelector("[data-ltn-nav-drawer]");

    this.toggle?.addEventListener("click", this.onToggleClick);
    this.drawer?.addEventListener("click", this.onDrawerClick);
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("click", this.onDocumentClick);
    window.addEventListener("resize", this.onResize, { passive: true });

    this.setOpen(false);
  }

  destroy(): void {
    this.toggle?.removeEventListener("click", this.onToggleClick);
    this.drawer?.removeEventListener("click", this.onDrawerClick);
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onDocumentClick);
    window.removeEventListener("resize", this.onResize);
  }

  setOpen(open: boolean): void {
    this.isOpen = open;
    this.toggle?.setAttribute("aria-expanded", String(open));
    this.drawer?.toggleAttribute("data-ltn-nav-hidden", !open);
  }
}

PluginRegistry.register("ltnNav", LtnNav);
